import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ScrollView,
} from 'react-native';
import { useTranslation } from 'react-i18next';
import { Club } from '../types';
import { Colors, Fonts, Radii, Shadows } from '../constants';
import { ClubLogo } from './ClubLogo';

interface ClubSelectorModalProps {
  clubs: Club[];
  selectedClub: string | null;
  onSelectClub: (clubId: string | null) => void;
}

export const ClubSelectorModal: React.FC<ClubSelectorModalProps> = ({
  clubs,
  selectedClub,
  onSelectClub,
}) => {
  const { t } = useTranslation();
  const [modalVisible, setModalVisible] = useState(false);

  const selectedClubData = clubs.find(club => club.id === selectedClub);

  const handleSelect = (clubId: string | null) => {
    onSelectClub(clubId);
    setModalVisible(false);
  };

  return (
    <View style={styles.container}>
      {/* Selector Trigger Button */}
      <TouchableOpacity
        style={styles.selectorButton}
        onPress={() => setModalVisible(true)}
      >
        {selectedClubData?.logo_url && (
          <ClubLogo logoUrl={selectedClubData.logo_url} size={32} style={styles.logoMargin} />
        )}
        <Text style={styles.selectorText}>
          {selectedClubData?.name || "All My Clubs"}
        </Text>
        <Text style={styles.chevron}>▼</Text>
      </TouchableOpacity>

      {/* Modal with Club List */}
      <Modal
        visible={modalVisible}
        transparent
        animationType="slide"
        onRequestClose={() => setModalVisible(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setModalVisible(false)}
        >
          <View style={styles.modalContent} onStartShouldSetResponder={() => true}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Club</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => setModalVisible(false)}
              >
                <Text style={styles.closeButtonText}>×</Text>
              </TouchableOpacity>
            </View>

            <ScrollView style={styles.clubList}>
              {/* "All Clubs" Option */}
              <TouchableOpacity
                style={[
                  styles.clubOption,
                  selectedClub === null && styles.selectedClubOption,
                ]}
                onPress={() => handleSelect(null)}
              >
                <View style={styles.clubOptionContent}>
                  <Text style={[styles.clubOptionText, selectedClub === null && styles.clubOptionTextSelected]}>
                    All My Clubs
                  </Text>
                  {selectedClub === null && <Text style={styles.checkmark}>✓</Text>}
                </View>
              </TouchableOpacity>

              {/* Individual Clubs */}
              {clubs.map(club => (
                <TouchableOpacity
                  key={club.id}
                  style={[
                    styles.clubOption,
                    selectedClub === club.id && styles.selectedClubOption,
                  ]}
                  onPress={() => handleSelect(club.id)}
                >
                  <View style={styles.clubOptionContent}>
                    {club.logo_url && (
                      <ClubLogo logoUrl={club.logo_url} size={32} style={styles.logoMargin} />
                    )}
                    <Text style={[styles.clubOptionText, selectedClub === club.id && styles.clubOptionTextSelected]}>
                      {club.name}
                    </Text>
                    {selectedClub === club.id && <Text style={styles.checkmark}>✓</Text>}
                  </View>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    marginBottom: 16,
  },
  selectorButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    backgroundColor: Colors.white,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.control,
  },
  logoMargin: {
    marginRight: 12,
  },
  selectorText: {
    flex: 1,
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.text,
  },
  chevron: {
    fontSize: 14,
    color: Colors.textMedium,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: Colors.overlay,
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: Colors.white,
    borderTopLeftRadius: Radii.dialog,
    borderTopRightRadius: Radii.dialog,
    maxHeight: '70%',
    paddingBottom: 20,
    ...Shadows.panel,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
  },
  modalTitle: {
    fontFamily: Fonts.display,
    fontSize: 22,
    lineHeight: 27,
    color: Colors.textDark,
  },
  closeButton: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  closeButtonText: {
    fontSize: 32,
    color: Colors.textMedium,
    lineHeight: 32,
  },
  clubList: {
    paddingHorizontal: 20,
  },
  clubOption: {
    paddingVertical: 12,
    paddingHorizontal: 12,
    borderRadius: Radii.thumb,
    borderBottomWidth: 1,
    borderBottomColor: Colors.borderLight,
  },
  selectedClubOption: {
    backgroundColor: Colors.primaryLight,
    borderBottomColor: 'transparent',
  },
  clubOptionContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  clubOptionText: {
    flex: 1,
    fontFamily: Fonts.regular,
    fontSize: 15,
    color: Colors.text,
  },
  clubOptionTextSelected: {
    fontFamily: Fonts.semiBold,
    color: Colors.accentStrong,
  },
  checkmark: {
    fontFamily: Fonts.semiBold,
    fontSize: 18,
    color: Colors.accentStrong,
  },
});
